import { deflateRaw } from './deflate';
import { encodeDocPackWordGeometry } from './geometry';
import {
  DOC_PACK_CONTENT_VERSION,
  DOC_PACK_MAGIC,
  DOC_PACK_READING_CLASS_DEFAULTS,
  DOC_PACK_SOURCE_VERSION,
  DocPackBuildInputError,
  type DocPackBuildInput,
  type DocPackCatalog,
  type DocPackCatalogPage,
  type DocPackChapterInput,
  type DocPackCharRange,
  type DocPackClassCount,
  type DocPackOcrCoord,
  type DocPackOcrWord,
  type DocPackPage,
  type DocPackPageBlock,
  type DocPackPageSegment,
  type DocPackPageSentence,
  type DocPackPageWord,
  type DocPackReadingClass,
  type DocPackRect,
  type DocPackSize,
  type DocPackSource,
} from './types';
import { Predicate } from 'effect';

const textEncoder = new TextEncoder();

const READING_CLASS_ALIASES: ReadonlyMap<string, DocPackReadingClass> = new Map(
  [
    ['body', 'body'],
    ['paragraph', 'body'],
    ['content', 'body'],
    ['heading', 'heading'],
    ['title', 'heading'],
    ['section-heading', 'heading'],
    ['citation', 'citation'],
    ['cite', 'citation'],
    ['cross-reference', 'cross-reference'],
    ['xref', 'cross-reference'],
    ['caption', 'caption'],
    ['figure-caption', 'caption'],
    ['table-caption', 'caption'],
    ['footnote', 'footnote'],
    ['endnote', 'endnote'],
    ['equation', 'equation'],
    ['formula', 'equation'],
    ['math', 'equation'],
    ['table', 'table'],
    ['header-footer', 'header-footer'],
    ['header', 'header-footer'],
    ['footer', 'header-footer'],
    ['page-number', 'page-number'],
    ['artifact', 'artifact'],
    ['reference-entry', 'reference-entry'],
    ['bibliography', 'reference-entry'],
    ['references', 'reference-entry'],
  ],
);

type PageDraft = {
  page: number;
  size: DocPackSize;
  textSource: 'born-digital' | 'ocr' | 'html';
  textParts: string[];
  textLength: number;
  words: DocPackPageWord[];
  sentences: DocPackPageSentence[];
  blocks: DocPackPageBlock[];
  segments: DocPackPageSegment[];
};

const coordsEqual = (a: DocPackOcrCoord, b: DocPackOcrCoord) =>
  a.page === b.page &&
  a.coords[0] === b.coords[0] &&
  a.coords[1] === b.coords[1] &&
  a.coords[2] === b.coords[2] &&
  a.coords[3] === b.coords[3];

/**
 * The coord the builder draws for a word and the page it assigns its text to.
 * A coord that duplicates the previous word's is a TTS expansion artifact
 * ("$1,000" → several spoken words sharing one OCR rect); an ALL-ZERO coord is
 * the aligner's "no matched OCR geometry" sentinel (inserted verbalizations).
 * Both are skipped so the drawn rect is `[]` (the "hidden" encoding), but the
 * word still pins to a page: the surviving coord's page, else the first coord's
 * page, else the word's own `page`. Shared with {@link maxObservedPage} so the
 * derived pageCount can never disagree with where a word actually lands.
 */
const resolveWordCoord = (
  word: DocPackOcrWord,
  prevCoords: readonly DocPackOcrCoord[],
): { coord: DocPackOcrCoord | undefined; page: number } => {
  const wordCoords = word.coords ?? [];
  const coord = wordCoords.find(
    (candidate) =>
      !(
        candidate.coords[0] === 0 &&
        candidate.coords[1] === 0 &&
        candidate.coords[2] === 0 &&
        candidate.coords[3] === 0
      ) && !prevCoords.some((prev) => coordsEqual(prev, candidate)),
  );
  return { coord, page: coord?.page ?? wordCoords[0]?.page ?? word.page };
};

/**
 * Highest page number any word in `chapters` resolves to (0 when there is no
 * geometry at all). The legacy backfill build uses this to DERIVE a pageCount
 * when `audio.page_count` is null/stale rather than trusting a wrong stored
 * value: `buildDocPackSource` fails loud when geometry references a page past
 * its `pageCount`, so the caller must pass a count that covers every word.
 */
export function maxObservedPage(
  chapters: readonly DocPackChapterInput[],
): number {
  let max = 0;
  for (const chapter of chapters) {
    for (const paragraph of chapter.paragraphs) {
      for (const sentence of paragraph.sentence_timestamps) {
        sentence.word_timestamps.forEach((word, wordIdx) => {
          const prevCoords =
            sentence.word_timestamps[wordIdx - 1]?.coords ?? [];
          const { page } = resolveWordCoord(word, prevCoords);
          if (page > max) max = page;
        });
      }
    }
  }
  return max;
}

const rectUnion = (rects: readonly DocPackRect[]): DocPackRect | null => {
  const first = rects[0];
  if (!first) return null;
  let x1 = first[0];
  let y1 = first[1];
  let x2 = first[2];
  let y2 = first[3];
  for (const rect of rects.slice(1)) {
    x1 = Math.min(x1, rect[0]);
    y1 = Math.min(y1, rect[1]);
    x2 = Math.max(x2, rect[2]);
    y2 = Math.max(y2, rect[3]);
  }
  return [x1, y1, x2, y2];
};

const normalizeReadingClass = (
  value: string | undefined,
): DocPackReadingClass | undefined => {
  if (!value) return;
  return READING_CLASS_ALIASES.get(value.trim().toLowerCase());
};

const paragraphText = (paragraph: {
  readonly sentence_timestamps: readonly {
    readonly word_timestamps: readonly { readonly text: string }[];
  }[];
}) =>
  paragraph.sentence_timestamps
    .flatMap((sentence) => sentence.word_timestamps.map((word) => word.text))
    .join(' ');

const classifyLegacyParagraph = (
  paragraph: DocPackBuildInput['chapters'][number]['paragraphs'][number],
  paragraphIdx: number,
): DocPackReadingClass => {
  const hinted = normalizeReadingClass(paragraph.reading_class);
  if (hinted) return hinted;
  if (paragraphIdx === 0) return 'heading';

  // These text heuristics are a stopgap for the legacy build path (pack built
  // from already-filtered chapter_details). Chapter-derived paragraphs default
  // to `read: true` regardless of class (see the segment push below), so a
  // misclassification here can no longer mute audio by itself — but it still
  // mislabels the class-toggle UI (a user hiding "captions" would hide the
  // paragraph), so stay conservative. The real fix is to build the doc-pack
  // from full OCR with explicit per-paragraph LLM reading_class labels (so
  // `hinted` above always wins) — see docs/projects/active/conversion-pipeline-v2.
  const text = paragraphText(paragraph).trim();
  if (/^\s*(references|bibliography)\s*$/i.test(text)) return 'heading';
  // Bracketed citation markers ("[12] Author, Title…") ONLY. Bare numeric list
  // forms ("1. Introduction", "2) Add the flour", "10. Results") are ordinary
  // numbered headings / list items far more often than reference entries and
  // must never be classified as reference entries from text alone — the same
  // reason the old `[a-z]\.` alternative ("a. First item") was removed.
  if (/^\s*\[\d+\]\s+/.test(text)) return 'reference-entry';
  // Caption/equation markers must be DELIMITED after the number ("Figure 2.
  // Overview", "Table 3: Results"). Running prose that merely opens with a
  // reference ("Table 5 shows that…", "Figure 3 illustrates…") is body text —
  // in legacy data it is almost always prose, because v1 filtered true
  // captions out of chapter_details at conversion time.
  if (/^\s*(figure|fig\.|table)\s+\d+\s*[.:—–-](\s|$)/i.test(text))
    return 'caption';
  if (/^\s*(equation|eq\.)\s+\d+\s*[.:—–-](\s|$)/i.test(text))
    return 'equation';
  // Intentionally no text-based page-number detection: a regex over roman
  // numerals matched ordinary short words ("Mild", "Civic", "Did") and muted
  // them. Page numbers belong to OCR layout/geometry, not paragraph text.

  return 'body';
};

const blockLabelForClass = (cls: DocPackReadingClass) => {
  if (cls === 'heading') return 'heading';
  if (cls === 'body') return 'paragraph';
  return cls;
};

const pageFor = (
  drafts: Map<number, PageDraft>,
  page: number,
  resolvePageSize: (page: number) => DocPackSize,
  textSource: 'born-digital' | 'ocr' | 'html',
): PageDraft => {
  const existing = drafts.get(page);
  if (existing) return existing;
  const created: PageDraft = {
    page,
    size: resolvePageSize(page),
    textSource,
    textParts: [],
    textLength: 0,
    words: [],
    sentences: [],
    blocks: [],
    segments: [],
  };
  drafts.set(page, created);
  return created;
};

const appendText = (draft: PageDraft, text: string): DocPackCharRange => {
  if (draft.textLength > 0) {
    draft.textParts.push(' ');
    draft.textLength += 1;
  }
  const start = draft.textLength;
  draft.textParts.push(text);
  draft.textLength += text.length;
  return [start, draft.textLength];
};

const buildPage = (draft: PageDraft): DocPackPage => ({
  page: draft.page,
  size: draft.size,
  textSource: draft.textSource,
  degraded: false,
  text: draft.textParts.join(''),
  geometry: encodeDocPackWordGeometry(draft.words),
  words: draft.words,
  sentences: draft.sentences,
  blocks: draft.blocks,
  segments: draft.segments,
});

const serializePage = (page: DocPackPage) =>
  textEncoder.encode(JSON.stringify(page));

export type BuiltDocPack = {
  readonly bytes: Uint8Array;
  readonly catalog: DocPackCatalog;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Predicate.isObjectOrArray(value);

const isNumberPair = (value: unknown): value is readonly [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  Predicate.isNumber(value[0]) &&
  Number.isFinite(value[0]) &&
  value[0] > 0 &&
  Predicate.isNumber(value[1]) &&
  Number.isFinite(value[1]) &&
  value[1] > 0;

const isReadingClass = (value: unknown): value is DocPackReadingClass =>
  Predicate.isString(value) &&
  Object.hasOwn(DOC_PACK_READING_CLASS_DEFAULTS, value);

const isTextSource = (value: unknown): value is DocPackPage['textSource'] =>
  value === 'born-digital' || value === 'ocr' || value === 'html';

const isValidCharRange = (value: unknown, textLength: number) =>
  Array.isArray(value) &&
  value.length === 2 &&
  Predicate.isNumber(value[0]) &&
  Number.isInteger(value[0]) &&
  value[0] >= 0 &&
  Predicate.isNumber(value[1]) &&
  Number.isInteger(value[1]) &&
  value[1] >= value[0] &&
  value[1] <= textLength;

const isValidRect = (value: unknown) =>
  Array.isArray(value) &&
  value.length === 4 &&
  value.every((coord) => Predicate.isNumber(coord) && Number.isFinite(coord)) &&
  value[2] >= value[0] &&
  value[3] >= value[1];

const validateAnchor = (
  value: unknown,
  pageNumber: number,
  textLength: number,
) => {
  if (!isObject(value))
    throw DocPackBuildInputError.make({
      message: 'doc-pack source word anchor must be an object',
    });

  if (value.type === 'rect') {
    if (value.page !== pageNumber)
      throw DocPackBuildInputError.make({
        message: 'doc-pack source rect anchor page must match the word page',
      });
    if (!Array.isArray(value.rects) || !value.rects.every(isValidRect))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source rect anchor rects must be valid rectangles',
      });
    return;
  }

  if (value.type === 'selector-offset') {
    if (!Predicate.isString(value.selector) || value.selector.length === 0) {
      throw DocPackBuildInputError.make({
        message:
          'doc-pack source selector-offset anchor selector must be a non-empty string',
      });
    }
    if (!isValidCharRange([value.startOffset, value.endOffset], textLength)) {
      throw DocPackBuildInputError.make({
        message: 'doc-pack source selector-offset anchor offsets are invalid',
      });
    }
    return;
  }

  throw DocPackBuildInputError.make({
    message: 'doc-pack source word anchor type is invalid',
  });
};

const validateOutlineItems = (
  items: readonly unknown[],
  pageTextLengths: ReadonlyMap<number, number>,
) => {
  for (const item of items) {
    if (!isObject(item))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source outline item must be an object',
      });
    if (!Predicate.isString(item.title))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source outline item title must be a string',
      });
    if (
      !Predicate.isNumber(item.page) ||
      !Number.isInteger(item.page) ||
      !pageTextLengths.has(item.page)
    ) {
      throw DocPackBuildInputError.make({
        message:
          'doc-pack source outline item page must reference a source page',
      });
    }
    if (
      item.chars !== undefined &&
      !isValidCharRange(item.chars, pageTextLengths.get(item.page) ?? 0)
    ) {
      throw DocPackBuildInputError.make({
        message: 'doc-pack source outline item chars are invalid',
      });
    }
    if (item.children !== undefined) {
      if (!Array.isArray(item.children))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source outline item children must be an array',
        });
      validateOutlineItems(item.children, pageTextLengths);
    }
  }
};

export function parseDocPackSource(value: unknown): DocPackSource {
  if (!isObject(value))
    throw DocPackBuildInputError.make({
      message: 'doc-pack source must be an object',
    });
  if (value.v !== DOC_PACK_SOURCE_VERSION)
    throw DocPackBuildInputError.make({
      message: `Unsupported doc-pack source version: ${String(value.v)}`,
    });
  if (!Predicate.isString(value.audioId) || value.audioId.length === 0)
    throw DocPackBuildInputError.make({
      message: 'doc-pack source audioId must be a non-empty string',
    });
  const pageCount = value.pageCount;
  if (
    !Predicate.isNumber(pageCount) ||
    !Number.isInteger(pageCount) ||
    pageCount < 1
  )
    throw DocPackBuildInputError.make({
      message: 'doc-pack source pageCount must be a positive integer',
    });
  if (!Array.isArray(value.pages))
    throw DocPackBuildInputError.make({
      message: 'doc-pack source pages must be an array',
    });
  if (!Array.isArray(value.outline))
    throw DocPackBuildInputError.make({
      message: 'doc-pack source outline must be an array',
    });
  if (value.pages.length !== pageCount)
    throw DocPackBuildInputError.make({
      message: 'doc-pack source pages length must match pageCount',
    });

  const pageTextLengths = new Map<number, number>();
  for (const page of value.pages) {
    if (!isObject(page))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page must be an object',
      });
    const pageNumber = page.page;
    if (
      !Predicate.isNumber(pageNumber) ||
      !Number.isInteger(pageNumber) ||
      pageNumber < 1
    )
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page number must be a positive integer',
      });
    if (pageNumber > pageCount)
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page number must not exceed pageCount',
      });
    if (pageTextLengths.has(pageNumber))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page numbers must be unique',
      });
    if (!isNumberPair(page.size))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page size must be a number pair',
      });
    if (!isTextSource(page.textSource))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page textSource is invalid',
      });
    if (!Predicate.isBoolean(page.degraded))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page degraded must be a boolean',
      });
    if (!Predicate.isString(page.text))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page text must be a string',
      });
    pageTextLengths.set(pageNumber, page.text.length);
    if (!Array.isArray(page.words))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page words must be an array',
      });
    if (!Array.isArray(page.sentences))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page sentences must be an array',
      });
    if (!Array.isArray(page.blocks))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page blocks must be an array',
      });
    if (!Array.isArray(page.segments))
      throw DocPackBuildInputError.make({
        message: 'doc-pack source page segments must be an array',
      });

    for (const word of page.words) {
      if (!isObject(word))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source word must be an object',
        });
      if (!Predicate.isString(word.text))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source word text must be a string',
        });
      if (!isValidCharRange(word.chars, page.text.length))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source word chars are invalid',
        });
      if (!Array.isArray(word.rects) || !word.rects.every(isValidRect))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source word rects must be valid rectangles',
        });
      validateAnchor(word.anchor, pageNumber, page.text.length);
    }

    for (const sentence of page.sentences) {
      if (!isObject(sentence))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source sentence must be an object',
        });
      if (!isValidCharRange(sentence.chars, page.text.length))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source sentence chars are invalid',
        });
    }

    for (const block of page.blocks) {
      if (!isObject(block))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block must be an object',
        });
      if (!Predicate.isString(block.id) || block.id.length === 0)
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block id must be a non-empty string',
        });
      if (!Predicate.isString(block.label) || block.label.length === 0)
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block label must be a non-empty string',
        });
      if (block.bbox !== null && !isValidRect(block.bbox))
        throw DocPackBuildInputError.make({
          message:
            'doc-pack source block bbox must be null or a valid rectangle',
        });
      if (!Predicate.isNumber(block.order) || !Number.isFinite(block.order))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block order must be a finite number',
        });
      if (
        block.chars !== null &&
        !isValidCharRange(block.chars, page.text.length)
      )
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block chars must be null or valid chars',
        });
      if (!isReadingClass(block.class))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block class is invalid',
        });
      if (block.artifact !== undefined && !Predicate.isBoolean(block.artifact))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source block artifact must be a boolean',
        });
    }

    for (const segment of page.segments) {
      if (!isObject(segment))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source segment must be an object',
        });
      if (!isReadingClass(segment.class))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source segment class is invalid',
        });
      if (!isValidCharRange(segment.chars, page.text.length))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source segment chars are invalid',
        });
      if (!Predicate.isBoolean(segment.read))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source segment read must be a boolean',
        });
      if (segment.blockId !== undefined && !Predicate.isString(segment.blockId))
        throw DocPackBuildInputError.make({
          message: 'doc-pack source segment blockId must be a string',
        });
    }
  }

  validateOutlineItems(value.outline, pageTextLengths);

  return value as DocPackSource;
}

const classCountsForPage = (
  page: DocPackPage,
): Partial<Record<DocPackReadingClass, DocPackClassCount>> => {
  const counts = new Map<
    DocPackReadingClass,
    { chars: number; words: number; offChars: number; offWords: number }
  >();
  const countsFor = (cls: DocPackReadingClass) => {
    const existing = counts.get(cls);
    if (existing) return existing;
    const created = { chars: 0, words: 0, offChars: 0, offWords: 0 };
    counts.set(cls, created);
    return created;
  };

  // Every count is split by the segment's read flag (always explicit —
  // buildDocPackSource resolves the class-default fallback when it emits the
  // segment, and parseDocPackSource requires a boolean): `chars`/`words` hold
  // read:true content, `offChars`/`offWords` hold read:false content. Playback
  // enables a segment via `override ?? segment.read` (reading-plan.ts
  // isDocPackClassEnabled), NOT the class default, so the catalog must keep
  // both buckets — a small-font body section arrives as read:false while
  // body's class default is read:true, and lumping it into `chars` made the
  // duration/position estimates disagree with what playback actually reads.

  // Assign each word to exactly ONE segment so per-class word totals never
  // exceed page.words.length (the catalog consumer in reading-plan.ts sums
  // these across classes to estimate reading duration; double-counting a word
  // that straddles a segment boundary inflates the estimate). The owning
  // segment is the first one whose char range contains the word's start
  // (word.chars[0]) — this mirrors how `chars` accumulates each segment's full
  // span and stays deterministic when segments happen to overlap.
  for (const word of page.words) {
    const owner = page.segments.find(
      (segment) =>
        word.chars[0] >= segment.chars[0] && word.chars[0] < segment.chars[1],
    );
    if (!owner) continue;
    const entry = countsFor(owner.class);
    if (owner.read) entry.words += 1;
    else entry.offWords += 1;
  }

  for (const segment of page.segments) {
    const entry = countsFor(segment.class);
    const length = segment.chars[1] - segment.chars[0];
    if (segment.read) entry.chars += length;
    else entry.offChars += length;
  }

  const result: Partial<Record<DocPackReadingClass, DocPackClassCount>> = {};
  for (const [cls, entry] of counts) {
    // Omit zero off buckets: keeps catalog JSON compact and shape-identical to
    // pre-split catalogs when a class has no read:false content.
    result[cls] = {
      chars: entry.chars,
      words: entry.words,
      ...(entry.offChars > 0 ? { offChars: entry.offChars } : {}),
      ...(entry.offWords > 0 ? { offWords: entry.offWords } : {}),
    };
  }
  return result;
};

export async function encodeDocPackSource(
  source: DocPackSource,
  contentVersion = DOC_PACK_CONTENT_VERSION,
): Promise<BuiltDocPack> {
  const sortedPages = source.pages.toSorted((a, b) => a.page - b.page);
  const blobs = await Promise.all(
    sortedPages.map(async (page) => ({
      page,
      bytes: await deflateRaw(serializePage(page)),
      classes: classCountsForPage(page),
    })),
  );

  let offset = 0;
  const pages: DocPackCatalogPage[] = [];
  for (const blob of blobs) {
    pages.push({
      n: blob.page.page,
      size: blob.page.size,
      offset,
      length: blob.bytes.length,
      textSource: blob.page.textSource,
      degraded: blob.page.degraded,
      classes: blob.classes,
    });
    offset += blob.bytes.length;
  }

  const catalog: DocPackCatalog = {
    v: 1,
    audioId: source.audioId,
    contentVersion,
    pageCount: source.pageCount,
    pages,
    outline: source.outline,
  };

  const catalogBytes = await deflateRaw(
    textEncoder.encode(JSON.stringify(catalog)),
  );
  const bytes = new Uint8Array(12 + catalogBytes.length + offset);
  bytes.set(textEncoder.encode(DOC_PACK_MAGIC), 0);
  new DataView(bytes.buffer).setUint32(8, catalogBytes.length, true);
  bytes.set(catalogBytes, 12);
  let cursor = 12 + catalogBytes.length;
  for (const blob of blobs) {
    bytes.set(blob.bytes, cursor);
    cursor += blob.bytes.length;
  }

  return { bytes, catalog };
}

export type DocPackWordLocation = {
  readonly page: number;
  readonly chars: DocPackCharRange;
};

export type DocPackWordLocations = ReadonlyMap<
  string,
  readonly (readonly (readonly DocPackWordLocation[])[])[]
>;

export function buildDocPackSource(input: DocPackBuildInput): DocPackSource {
  return buildSource(input, undefined);
}

export function buildDocPackSourceWithWordLocations(input: DocPackBuildInput): {
  readonly source: DocPackSource;
  readonly wordLocations: DocPackWordLocations;
} {
  const wordLocations = new Map<string, DocPackWordLocation[][][]>();
  return { source: buildSource(input, wordLocations), wordLocations };
}

function buildSource(
  input: DocPackBuildInput,
  wordLocations: Map<string, DocPackWordLocation[][][]> | undefined,
): DocPackSource {
  const pageDrafts = new Map<number, PageDraft>();
  const outline: DocPackCatalog['outline'][number][] = [];
  const textSource = input.textSource ?? 'ocr';
  // Resolve a page's size from the per-page map, falling back to the default.
  // When `pageSizes` is absent this always returns `defaultPageSize`, keeping
  // output byte-identical to the previous single-size behavior.
  const resolvePageSize = (page: number): DocPackSize =>
    input.pageSizes?.get(page) ?? input.defaultPageSize;
  let blockOrder = 0;

  for (const chapter of input.chapters) {
    let chapterFirstPage: number | null = null;
    let chapterFirstChars: DocPackCharRange | undefined;
    const chapterLocations: DocPackWordLocation[][][] | undefined =
      wordLocations ? [] : undefined;
    if (chapterLocations) wordLocations?.set(chapter.id, chapterLocations);

    chapter.paragraphs.forEach((paragraph, paragraphIdx) => {
      const blockClass = classifyLegacyParagraph(paragraph, paragraphIdx);
      const paragraphByPage = new Map<
        number,
        {
          rects: DocPackRect[];
          firstChar: number;
          lastChar: number;
          runs: { class: DocPackReadingClass; chars: [number, number] }[];
        }
      >();
      const paragraphLocations: DocPackWordLocation[][] | undefined =
        chapterLocations ? [] : undefined;
      if (paragraphLocations) chapterLocations?.push(paragraphLocations);

      paragraph.sentence_timestamps.forEach((sentence) => {
        const sentenceByPage = new Map<
          number,
          { firstChar: number; lastChar: number }
        >();
        const sentenceLocations: DocPackWordLocation[] | undefined =
          paragraphLocations ? [] : undefined;
        if (sentenceLocations) paragraphLocations?.push(sentenceLocations);

        sentence.word_timestamps.forEach((word, wordIdx) => {
          const prevCoords =
            sentence.word_timestamps[wordIdx - 1]?.coords ?? [];
          // `coord` is the surviving OCR rect (or undefined → the "hidden"
          // `rects: []` encoding: text/chars/anchor stay intact so the shared
          // rect draws exactly one box instead of stacked duplicates or a
          // degenerate [0,0,0,0]); `pageNumber` still pins the word to its
          // page. See resolveWordCoord for the skip rules.
          const { coord, page: pageNumber } = resolveWordCoord(
            word,
            prevCoords,
          );
          const draft = pageFor(
            pageDrafts,
            pageNumber,
            resolvePageSize,
            textSource,
          );
          const chars = appendText(draft, word.text);
          sentenceLocations?.push({ page: pageNumber, chars });
          const rects = coord ? [coord.coords] : [];
          const anchor =
            textSource === 'html' && rects.length === 0
              ? ({
                  type: 'selector-offset',
                  selector: `${chapter.id}:p${paragraphIdx.toString()}`,
                  startOffset: chars[0],
                  endOffset: chars[1],
                } as const)
              : ({ type: 'rect', page: pageNumber, rects } as const);
          draft.words.push({
            text: word.text,
            chars,
            rects,
            anchor,
          });

          const sentenceDraft = sentenceByPage.get(pageNumber);
          if (sentenceDraft) {
            sentenceDraft.lastChar = chars[1];
          } else {
            sentenceByPage.set(pageNumber, {
              firstChar: chars[0],
              lastChar: chars[1],
            });
          }

          const wordClass =
            paragraph.read === false
              ? blockClass
              : (word.reading_class ?? blockClass);
          const paragraphDraft = paragraphByPage.get(pageNumber);
          if (paragraphDraft) {
            paragraphDraft.rects.push(...rects);
            paragraphDraft.lastChar = chars[1];
            const run = paragraphDraft.runs.at(-1);
            if (run?.class === wordClass) run.chars[1] = chars[1];
            else
              paragraphDraft.runs.push({
                class: wordClass,
                chars: [chars[0], chars[1]],
              });
          } else {
            paragraphByPage.set(pageNumber, {
              rects: [...rects],
              firstChar: chars[0],
              lastChar: chars[1],
              runs: [{ class: wordClass, chars: [chars[0], chars[1]] }],
            });
          }

          chapterFirstPage ??= pageNumber;
          chapterFirstChars ??= chars;
        });

        for (const [page, chars] of sentenceByPage) {
          pageFor(pageDrafts, page, resolvePageSize, textSource).sentences.push(
            {
              chars: [chars.firstChar, chars.lastChar],
            },
          );
        }
      });

      for (const [page, paragraphPage] of paragraphByPage) {
        const draft = pageFor(pageDrafts, page, resolvePageSize, textSource);
        const chars = [
          paragraphPage.firstChar,
          paragraphPage.lastChar,
        ] as const;
        const blockId = `${chapter.id}:p${paragraphIdx.toString()}:pg${page.toString()}`;
        draft.blocks.push({
          id: blockId,
          label: blockLabelForClass(blockClass),
          bbox: rectUnion(paragraphPage.rects),
          order: blockOrder++,
          chars,
          class: blockClass,
        });
        // One segment per run of same-class words, all in the paragraph's
        // block: a paragraph without inline classes is one segment spanning it.
        for (const run of paragraphPage.runs) {
          draft.segments.push({
            class: run.class,
            chars: run.chars,
            // Honor an explicit per-paragraph read decision (the full-OCR build
            // always sets one). Paragraphs WITHOUT it are chapter-derived (the
            // legacy backfill and the non-OCR fallback build) — everything in
            // chapter_details was spoken by the legacy player, so they default
            // to read:true. Falling back to the class default here would mute
            // audio production reads (e.g. a hinted or heuristic `caption`);
            // classes on chapter-derived paragraphs are annotation-only. An
            // inline run of another class reads by that class's default.
            read:
              run.class === blockClass
                ? (paragraph.read ?? true)
                : DOC_PACK_READING_CLASS_DEFAULTS[run.class],
            blockId,
          });
        }
      }
    });

    outline.push({
      title: chapter.title,
      page: chapterFirstPage ?? 1,
      chars: chapterFirstChars,
    });
  }

  // pageCount is authoritative from input.pageCount (audio.page_count = PDF
  // metadata). OCR geometry referencing a page BEYOND it must fail loud, not
  // silently extend the pack: an over-count pack runs past audio.page_count, and
  // the notes/progress/page-image endpoints (which validate anchors against
  // audio.page_count) then reject valid references into the extra pages. An
  // out-of-range page means corrupt OCR/geometry input, so we refuse it here
  // rather than papering over it (owner decision, round 4). Filling 1..pageCount
  // still keeps the reader/parseDocPackSource invariants (unique page numbers,
  // all <= pageCount, pages.length === pageCount).
  const observedMaxPage =
    pageDrafts.size > 0 ? Math.max(...pageDrafts.keys()) : 0;
  if (observedMaxPage > input.pageCount) {
    throw DocPackBuildInputError.make({
      message: `doc-pack build: OCR geometry references page ${observedMaxPage} but the document has only ${input.pageCount} page(s) (audio=${input.audioId}); refusing to extend the pack past audio.page_count`,
    });
  }
  const pageCount = input.pageCount;
  for (let page = 1; page <= pageCount; page++) {
    pageFor(pageDrafts, page, resolvePageSize, textSource);
  }

  const sortedPages = [...pageDrafts.values()].toSorted(
    (a, b) => a.page - b.page,
  );

  return {
    v: DOC_PACK_SOURCE_VERSION,
    audioId: input.audioId,
    pageCount,
    pages: sortedPages.map(buildPage),
    outline,
  };
}

export function buildDocPack(input: DocPackBuildInput): Promise<BuiltDocPack> {
  return encodeDocPackSource(buildDocPackSource(input));
}
